import { useState, useMemo } from 'react'
import { X, AlertTriangle, ChevronRight, ChevronLeft, Loader2, FileText, CheckCircle, HardDrive } from 'lucide-react'
import { pepApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * PepChiusuraModal — Wizard 3 step per chiudere un PEP
 *
 * Step 1: Motivo + esiti obiettivi attivi
 * Step 2: Sintesi finale (pre-compilata) + raccomandazioni
 * Step 3: Riepilogo + confronto + opzioni PDF + conferma
 *
 * Props:
 *   pep         - Oggetto PEP attivo completo
 *   minoreNome  - Nome del minore
 *   onClose     - callback chiusura
 *   onDone      - callback dopo chiusura PEP (per reload)
 */

const MOTIVO_OPTIONS = [
  { value: 'obiettivi_raggiunti', label: 'Obiettivi raggiunti' },
  { value: 'trasferimento', label: 'Trasferimento ad altro centro' },
  { value: 'dimissione', label: 'Dimissione dal centro' },
  { value: 'cambio_progetto', label: 'Cambio progetto (nuovo PEP)' },
  { value: 'fine_mandato', label: 'Fine mandato servizi sociali' },
  { value: 'altro', label: 'Altro' }
]

const ESITO_OPTIONS = [
  { value: 'raggiunto', label: 'Raggiunto', color: 'text-green-700 bg-green-50' },
  { value: 'parzialmente_raggiunto', label: 'Parziale', color: 'text-orange-700 bg-orange-50' },
  { value: 'non_raggiunto', label: 'Non raggiunto', color: 'text-red-700 bg-red-50' },
  { value: 'sospeso', label: 'Sospeso', color: 'text-gray-700 bg-gray-50' }
]

const AREA_LABELS = {
  autonomia: 'Autonomia', socializzazione: 'Socializzazione',
  gestione_emotiva: 'Gestione emotiva', apprendimento: 'Apprendimento',
  relazioni_familiari: 'Relazioni familiari', comportamento: 'Comportamento',
  cura_di_se: 'Cura di sé', rispetto_regole: 'Rispetto regole',
  motivazione: 'Motivazione', altro: 'Altro'
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * Genera suggerimento sintesi dai dati PEP (template, NON AI)
 */
function generaSuggerimentoSintesi(pep, minoreNome) {
  const obiettivi = pep.obiettivi || []
  const colloqui = pep.colloqui || []
  const obRaggiunti = obiettivi.filter(o => o.stato === 'raggiunto').length
  const obTotali = obiettivi.length
  const durata = pep.durataMesi || Math.round(
    (Date.now() - new Date(pep.dataAvvio).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
  )

  const enteInfo = pep.contestoIniziale?.enteInviante
  const ente = enteInfo?.tipo === 'servizi_sociali' ? 'dei Servizi Sociali'
    : enteInfo?.tipo === 'tribunale_minori' ? 'del Tribunale dei Minori'
    : enteInfo?.nome ? `di ${enteInfo.nome}`
    : "dell'ente inviante"

  let testo = `${minoreNome} è stato inserito nel centro in data ${formatDate(pep.dataAvvio)} su segnalazione ${ente}. `
  testo += `Il percorso educativo ha avuto una durata di ${durata} mesi. `
  testo += `Sono stati definiti ${obTotali} obiettivi educativi, di cui ${obRaggiunti} raggiunti. `

  if (colloqui.length > 0) {
    const tipi = [...new Set(colloqui.map(c => c.tipo?.replace(/_/g, ' ')).filter(Boolean))]
    testo += `Nel corso del percorso sono stati effettuati ${colloqui.length} colloqui/incontri`
    if (tipi.length > 0) testo += ` (${tipi.slice(0, 3).join(', ')})`
    testo += '. '
  }

  testo += '\n\n[Completare con valutazione qualitativa del percorso, punti di forza emersi, difficoltà incontrate e risultati ottenuti.]'
  return testo
}

export default function PepChiusuraModal({ pep, minoreNome, onClose, onDone }) {
  const { showSuccess, showError } = useNotification()
  const { tenant } = useAuth()
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)

  // Step 1
  const [motivoChiusura, setMotivoChiusura] = useState('')
  const obiettiviAttivi = useMemo(() => (pep.obiettivi || []).filter(o => o.stato === 'attivo'), [pep])
  const [esitiObiettivi, setEsitiObiettivi] = useState(() =>
    obiettiviAttivi.map(ob => ({ obiettivoId: ob._id, esitoFinale: 'parzialmente_raggiunto', nota: '' }))
  )

  // Step 2
  const [sintesiFinale, setSintesiFinale] = useState(() => generaSuggerimentoSintesi(pep, minoreNome))
  const [raccomandazioni, setRaccomandazioni] = useState('')

  // Step 3
  const [generaPdf, setGeneraPdf] = useState(true)
  const [saveToDrive, setSaveToDrive] = useState(false)
  const isDriveConfigured = !!tenant?.settings?.googleDriveConfigured

  // Riepilogo per step 1
  const obRaggiuntiCount = (pep.obiettivi || []).filter(o => o.stato === 'raggiunto').length
  const obParzialiCount = (pep.obiettivi || []).filter(o => o.stato === 'parzialmente_raggiunto').length

  const updateEsito = (idx, field, value) => {
    setEsitiObiettivi(prev => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      return next
    })
  }

  // Validation
  const canGoStep2 = motivoChiusura !== ''
  const canGoStep3 = sintesiFinale.trim().length >= 50

  const handleSubmit = async (withPdf) => {
    setSubmitting(true)
    try {
      const payload = {
        motivoChiusura,
        sintesiFinale: sintesiFinale.trim(),
        raccomandazioni: raccomandazioni.trim() || undefined,
        obiettiviChiusura: esitiObiettivi,
        generaPdf: withPdf,
        ...(withPdf && saveToDrive && { saveToGoogleDrive: true })
      }

      const response = await pepApi.chiudi(pep._id, payload)

      // Se PDF, scarica il blob
      if (withPdf && response.data instanceof Blob) {
        const blob = response.data
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const cd = response.headers?.['content-disposition'] || ''
        const match = cd.match(/filename="?([^"]+)"?/)
        link.download = match?.[1] || `PEP_chiusura_${new Date().toISOString().slice(0, 10)}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        const driveFileId = response.headers?.['x-google-drive-file-id']
        showSuccess(driveFileId ? 'PEP chiuso, PDF scaricato e salvato su Google Drive' : 'PEP chiuso e PDF scaricato')
      } else {
        // JSON response (might be blob if PDF failed)
        if (response.data instanceof Blob) {
          try {
            const text = await response.data.text()
            const json = JSON.parse(text)
            if (json.data?.pdfError) {
              showSuccess('PEP chiuso. Errore nel PDF, potrai scaricarlo dalla scheda.')
            } else {
              showSuccess(json.message || 'PEP chiuso con successo')
            }
          } catch {
            showSuccess('PEP chiuso con successo')
          }
        } else {
          showSuccess(response.data?.message || 'PEP chiuso con successo')
        }
      }

      onDone?.()
      onClose()
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const json = JSON.parse(text)
          showError(json.message || 'Errore nella chiusura')
        } catch {
          showError('Errore nella chiusura del PEP')
        }
      } else {
        showError(error.response?.data?.message || 'Errore nella chiusura del PEP')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Confronto inizio/fine per step 3
  const confronto = useMemo(() => {
    const rows = []
    for (const ob of (pep.obiettivi || [])) {
      if (ob.indicatore?.tipo === 'automatico' && ob.indicatore?.valoreBaseline != null) {
        const label = ob.indicatore.etichettaValore || ''
        const attuale = ob.indicatore?.valoreAttuale
        rows.push({
          dimensione: ob.titolo,
          ingresso: `${ob.indicatore.valoreBaseline} ${label}`,
          chiusura: attuale != null ? `${attuale} ${label}` : '—'
        })
      }
    }
    return rows
  }, [pep])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 w-full max-w-2xl my-4 mx-4 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Chiusura Progetto Educativo
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{minoreNome} — Step {step} di 3</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper dots */}
        <div className="flex items-center justify-center gap-2 py-3 border-b border-gray-100 dark:border-gray-700">
          {[1, 2, 3].map(s => (
            <div key={s} className={`w-2.5 h-2.5 rounded-full transition-colors ${
              s === step ? 'bg-primary-500' : s < step ? 'bg-primary-300' : 'bg-gray-300 dark:bg-gray-600'
            }`} />
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[calc(100vh-250px)] overflow-y-auto">
          {step === 1 && <Step1
            motivoChiusura={motivoChiusura}
            setMotivoChiusura={setMotivoChiusura}
            obiettiviAttivi={obiettiviAttivi}
            esitiObiettivi={esitiObiettivi}
            updateEsito={updateEsito}
            obRaggiuntiCount={obRaggiuntiCount}
            obParzialiCount={obParzialiCount}
          />}
          {step === 2 && <Step2
            sintesiFinale={sintesiFinale}
            setSintesiFinale={setSintesiFinale}
            raccomandazioni={raccomandazioni}
            setRaccomandazioni={setRaccomandazioni}
          />}
          {step === 3 && <Step3
            pep={pep}
            motivoChiusura={motivoChiusura}
            esitiObiettivi={esitiObiettivi}
            obiettiviAttivi={obiettiviAttivi}
            sintesiFinale={sintesiFinale}
            raccomandazioni={raccomandazioni}
            confronto={confronto}
            generaPdf={generaPdf}
            setGeneraPdf={setGeneraPdf}
            saveToDrive={saveToDrive}
            setSaveToDrive={setSaveToDrive}
            isDriveConfigured={isDriveConfigured}
          />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            {step > 1 && (
              <button
                onClick={() => setStep(s => s - 1)}
                disabled={submitting}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <ChevronLeft className="h-4 w-4" />
                Indietro
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {step < 3 && (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={step === 1 ? !canGoStep2 : !canGoStep3}
                className="flex items-center gap-1 text-sm font-medium bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Avanti
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            {step === 3 && (
              <>
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={submitting}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-2"
                >
                  Chiudi senza PDF
                </button>
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={submitting}
                  className="flex items-center gap-2 text-sm font-medium bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Chiudi e genera PDF
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Step 1: Motivo + Esiti obiettivi ─────────────────────────

function Step1({ motivoChiusura, setMotivoChiusura, obiettiviAttivi, esitiObiettivi, updateEsito, obRaggiuntiCount, obParzialiCount }) {
  return (
    <div className="space-y-5">
      {/* Motivo chiusura */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Motivo della chiusura *
        </label>
        <select
          value={motivoChiusura}
          onChange={(e) => setMotivoChiusura(e.target.value)}
          className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Seleziona il motivo...</option>
          {MOTIVO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Riepilogo automatico */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium text-green-600">{obRaggiuntiCount} raggiunti</span>
          {obParzialiCount > 0 && <>, <span className="font-medium text-orange-600">{obParzialiCount} parziali</span></>}
          {obiettiviAttivi.length > 0 && <>, <span className="font-medium text-blue-600">{obiettiviAttivi.length} attivi da chiudere</span></>}
        </p>
      </div>

      {/* Esiti obiettivi attivi */}
      {obiettiviAttivi.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Chiudi gli obiettivi ancora attivi
          </label>
          <div className="space-y-3">
            {obiettiviAttivi.map((ob, idx) => (
              <div key={ob._id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                    {AREA_LABELS[ob.area] || ob.area}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{ob.titolo}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={esitiObiettivi[idx]?.esitoFinale || 'parzialmente_raggiunto'}
                    onChange={(e) => updateEsito(idx, 'esitoFinale', e.target.value)}
                    className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {ESITO_OPTIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                  <input
                    type="text"
                    value={esitiObiettivi[idx]?.nota || ''}
                    onChange={(e) => updateEsito(idx, 'nota', e.target.value)}
                    placeholder="Nota breve (opzionale)"
                    maxLength={1000}
                    className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Step 2: Sintesi + Raccomandazioni ────────────────────────

function Step2({ sintesiFinale, setSintesiFinale, raccomandazioni, setRaccomandazioni }) {
  const charCount = sintesiFinale.trim().length
  const isValid = charCount >= 50

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Sintesi del percorso *
          <span className="text-xs font-normal text-gray-400 ml-1">(min 50 caratteri)</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">
          La relazione finale per i servizi sociali. Il testo suggerito sotto è generato dai dati del PEP — modificalo o cancellalo.
        </p>
        <textarea
          value={sintesiFinale}
          onChange={(e) => setSintesiFinale(e.target.value)}
          maxLength={5000}
          rows={8}
          className={`w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y ${
            !isValid && charCount > 0 ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-600'
          }`}
          placeholder="Descrivi il percorso educativo, i risultati ottenuti, le difficoltà incontrate..."
        />
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${isValid ? 'text-green-600' : 'text-gray-400'}`}>
            {charCount}/5000 {!isValid && charCount > 0 && `(servono almeno ${50 - charCount} caratteri)`}
          </span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Raccomandazioni
          <span className="text-xs font-normal text-gray-400 ml-1">(opzionale)</span>
        </label>
        <textarea
          value={raccomandazioni}
          onChange={(e) => setRaccomandazioni(e.target.value)}
          maxLength={3000}
          rows={4}
          className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y"
          placeholder="Raccomandazioni per il futuro del minore, indicazioni per eventuale presa in carico da altro servizio..."
        />
        <p className="text-xs text-gray-400 text-right mt-1">{raccomandazioni.length}/3000</p>
      </div>
    </div>
  )
}

// ── Step 3: Riepilogo + Confronto + Conferma ─────────────────

function Step3({ pep, motivoChiusura, esitiObiettivi, obiettiviAttivi, sintesiFinale, raccomandazioni, confronto, generaPdf, setGeneraPdf, saveToDrive, setSaveToDrive, isDriveConfigured }) {
  const motivoLabel = MOTIVO_OPTIONS.find(m => m.value === motivoChiusura)?.label || motivoChiusura
  const durata = pep.durataMesi || Math.round(
    (Date.now() - new Date(pep.dataAvvio).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
  )

  return (
    <div className="space-y-5">
      {/* Riepilogo */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Riepilogo</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <span className="text-gray-500">Motivo:</span>
          <span className="text-gray-900 dark:text-white font-medium">{motivoLabel}</span>
          <span className="text-gray-500">Durata:</span>
          <span className="text-gray-900 dark:text-white">{durata} mesi</span>
          <span className="text-gray-500">Obiettivi totali:</span>
          <span className="text-gray-900 dark:text-white">{(pep.obiettivi || []).length}</span>
          <span className="text-gray-500">Colloqui:</span>
          <span className="text-gray-900 dark:text-white">{(pep.colloqui || []).length}</span>
        </div>

        {/* Esiti obiettivi da chiudere */}
        {obiettiviAttivi.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs font-medium text-gray-500 mb-1">Obiettivi da chiudere:</p>
            {obiettiviAttivi.map((ob, idx) => {
              const esito = esitiObiettivi[idx]
              const esitoLabel = ESITO_OPTIONS.find(e => e.value === esito?.esitoFinale)?.label || esito?.esitoFinale
              return (
                <p key={ob._id} className="text-xs text-gray-600 dark:text-gray-400">
                  {ob.titolo}: <span className="font-medium">{esitoLabel}</span>
                  {esito?.nota && ` — ${esito.nota}`}
                </p>
              )
            })}
          </div>
        )}
      </div>

      {/* Confronto inizio/fine */}
      {confronto.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Confronto inizio — fine</h4>
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Dimensione</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Ingresso</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Chiusura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {confronto.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-gray-900 dark:text-white">{r.dimensione}</td>
                    <td className="px-3 py-2 text-gray-500">{r.ingresso}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{r.chiusura}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">
            I valori di ingresso si basano sulle valutazioni iniziali registrate nel progetto educativo.
          </p>
        </div>
      )}

      {/* Anteprima sintesi */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Sintesi finale</h4>
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-4 whitespace-pre-line">
          {sintesiFinale.substring(0, 300)}{sintesiFinale.length > 300 && '...'}
        </p>
      </div>

      {raccomandazioni.trim() && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Raccomandazioni</h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3">
            {raccomandazioni.substring(0, 200)}{raccomandazioni.length > 200 && '...'}
          </p>
        </div>
      )}

      {/* Toggle PDF */}
      <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer">
        <input
          type="checkbox"
          checked={generaPdf}
          onChange={(e) => {
            setGeneraPdf(e.target.checked)
            if (!e.target.checked) setSaveToDrive(false)
          }}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Genera PDF di chiusura</p>
          <p className="text-xs text-gray-500">Il documento verrà scaricato automaticamente</p>
        </div>
      </label>

      {/* Toggle Google Drive — visibile solo se PDF attivo e Drive configurato */}
      {generaPdf && isDriveConfigured && (
        <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer ml-6">
          <input
            type="checkbox"
            checked={saveToDrive}
            onChange={(e) => setSaveToDrive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Salva PDF su Google Drive</p>
              <p className="text-xs text-gray-500">Una copia verrà salvata nella cartella del centro</p>
            </div>
          </div>
        </label>
      )}

      {/* Warning */}
      <div className="flex gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-lg">
        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-800 dark:text-red-300">
            Questa azione è irreversibile
          </p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
            Il PEP verrà chiuso e non sarà più modificabile. Potrai comunque consultarlo e scaricarne il PDF.
          </p>
        </div>
      </div>
    </div>
  )
}
